// Typed admin API client. Token is read from sessionStorage on each call so
// rotating it (logout) takes effect on the next request without React state.

import type {
  AuditRow,
  EventRow,
  FormConfig,
  Site,
  StatRow,
} from "./types";

const TOKEN_KEY = "mailadmin.token";

export function setToken(t: string): void {
  sessionStorage.setItem(TOKEN_KEY, t);
}
export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}
export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

export interface ApiError extends Error {
  status: number;
  error: string;
  errors?: Record<string, string>;
}

function makeError(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as { error?: string; errors?: Record<string, string> };
  const err = new Error(b.error ?? `http_${status}`) as ApiError;
  err.status = status;
  err.error = b.error ?? `http_${status}`;
  err.errors = b.errors;
  return err;
}

async function request<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const tok = getToken();
  const headers: Record<string, string> = {};
  if (tok) headers["Authorization"] = `Bearer ${tok}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: "no-store",
  });
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    // Empty / non-JSON body is OK for some 204s.
  }
  if (!res.ok) throw makeError(res.status, json);
  return json as T;
}

export const api = {
  whoami: () => request<{ ok: true }>("GET", "/v1/admin/whoami"),

  listSites: () => request<{ ok: true; sites: Site[] }>("GET", "/v1/admin/sites"),
  getSite: (k: string) =>
    request<{ ok: true; site: Site }>(
      "GET",
      `/v1/admin/sites/${encodeURIComponent(k)}`,
    ),
  createSite: (b: { siteKey: string; name: string; enabled?: boolean }) =>
    request<{ ok: true; site: Site }>("POST", "/v1/admin/sites", b),
  updateSite: (k: string, b: Partial<{ name: string; enabled: boolean }>) =>
    request<{ ok: true; site: Site }>(
      "PATCH",
      `/v1/admin/sites/${encodeURIComponent(k)}`,
      b,
    ),
  deleteSite: (k: string) =>
    request<{ ok: true }>("DELETE", `/v1/admin/sites/${encodeURIComponent(k)}`),
  listFormsForSite: (k: string) =>
    request<{ ok: true; forms: FormConfig[] }>(
      "GET",
      `/v1/admin/sites/${encodeURIComponent(k)}/forms`,
    ),

  listForms: () => request<{ ok: true; forms: FormConfig[] }>("GET", "/v1/admin/forms"),
  getForm: (k: string) =>
    request<{ ok: true; form: FormConfig }>(
      "GET",
      `/v1/admin/forms/${encodeURIComponent(k)}`,
    ),
  createForm: (b: Partial<FormConfig> & { formKey: string; siteKey: string }) =>
    request<{ ok: true; form: FormConfig }>("POST", "/v1/admin/forms", b),
  updateForm: (k: string, b: Partial<FormConfig>) =>
    request<{ ok: true; form: FormConfig }>(
      "PATCH",
      `/v1/admin/forms/${encodeURIComponent(k)}`,
      b,
    ),
  deleteForm: (k: string) =>
    request<{ ok: true }>("DELETE", `/v1/admin/forms/${encodeURIComponent(k)}`),
  killForm: (k: string, on: boolean) =>
    request<{ ok: true; killed: boolean }>(
      "POST",
      `/v1/admin/forms/${encodeURIComponent(k)}/kill`,
      { on },
    ),

  stats: (days = 7, formKey?: string) => {
    const q = new URLSearchParams({ days: String(days) });
    if (formKey) q.set("formKey", formKey);
    return request<{ ok: true; days: number; results: StatRow[] }>(
      "GET",
      `/v1/admin/stats?${q}`,
    );
  },
  recent: (limit = 50, formKey?: string) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (formKey) q.set("formKey", formKey);
    return request<{ ok: true; results: EventRow[] }>(
      "GET",
      `/v1/admin/recent?${q}`,
    );
  },
  audit: (limit = 100) =>
    request<{ ok: true; results: AuditRow[] }>(
      "GET",
      `/v1/admin/audit?limit=${limit}`,
    ),
};

/** Generate a 26-char base32 form key client-side (server validates `[A-Z2-7]{26}`). */
export function generateFormKey(): string {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  const bytes = crypto.getRandomValues(new Uint8Array(26));
  let out = "";
  for (let i = 0; i < 26; i++) out += alphabet[bytes[i] % 32];
  return out;
}
